import React from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import {
    FlexBox,
    Orientation,
    DatePickerField,
    FlexBoxProps,
    DatePickerFieldProps,
    InputHelper,
    Kind,
} from '@lumapps/lumx/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';
import { isToday } from '@lumapps/utils/date/isToday';
import { getTimeZoneOffset } from '@lumapps/utils/timezones/getTimeZoneOffset';
import { Time, TimeZone } from '@lumapps/utils/types/Time';

import { TimePickerField, TimePickerFieldProps } from '../TimePickerField';
import { TimeZonePickerField, TimeZonePickerFieldProps } from '../TimeZonePickerField';
import {
    getSelectedDateTimeZone,
    GetSelectedDateTimeZone,
    getTimestampFromDateTimeZone,
    GetSelectedDateTimeZoneOptions,
    getDefaultDateTimeZone,
} from './utils';

import './index.scss';

export interface DateTimeZonePickerFieldProps {
    /** timestamp to display, in UTC */
    timestamp?: string;
    /** timezone, in the format Europe/Paris */
    timeZone?: string;
    /** current user's language */
    language: string;
    /** callback when the timestamp changes. timestamp returned will be in UTC */
    onChange: (props: { timestamp?: string; data: GetSelectedDateTimeZone }) => void;
    /** additional props for the flexbox wrapper */
    wrapperProps?: Partial<FlexBoxProps>;
    /** additional props for the date picker */
    datePickerProps?: Partial<DatePickerFieldProps>;
    /** additional props for the time picker */
    timePickerProps?: Partial<TimePickerFieldProps>;
    /** additional props for the time zone picker */
    timeZonePickerProps?: Partial<TimeZonePickerFieldProps>;
    /** whether dates in the past should be disabled or not  */
    disableOptionsInThePast?: boolean;
    /** offset that the default time will be displayed with */
    defaultHourOffset?: GetSelectedDateTimeZoneOptions['defaultHourOffset'];
    /** whether the component should display the date picker or not */
    withDate?: boolean;
    /** the current error message */
    error?: string;
    /** whether we should fallback to current date time when none provided */
    shouldFallbackToCurrentDateTime?: boolean;
    /** orientation of the components */
    orientation?: Orientation;
}

const CLASSNAME = 'date-time-zone-picker';
/**
 * Component that combines the date, time and timezone picker into a single component
 * @family Pickers
 */
export const DateTimeZonePickerField: React.FC<DateTimeZonePickerFieldProps> = ({
    timestamp,
    timeZone,
    language,
    onChange,
    wrapperProps,
    datePickerProps,
    timePickerProps,
    timeZonePickerProps,
    disableOptionsInThePast,
    defaultHourOffset,
    withDate = true,
    error,
    shouldFallbackToCurrentDateTime = true,
    orientation = Orientation.horizontal,
}) => {
    const values = React.useMemo(
        () =>
            getSelectedDateTimeZone({
                timestamp,
                timeZone,
                defaultHourOffset,
                shouldFallbackToCurrentDateTime,
            }),
        [defaultHourOffset, shouldFallbackToCurrentDateTime, timeZone, timestamp],
    );

    const { translateKey } = useTranslate();
    const { element, block } = useClassnames(CLASSNAME);
    const [date, setDate] = React.useState<Date | undefined>(values.date);
    const [time, setTime] = React.useState<Time | undefined>(values.time);
    const [selectedTimeZone, setSelectedTimeZone] = React.useState<TimeZone>(values.timeZone);

    /**
     * If the props change, we need to recalculate the selected values and set them once more.
     */
    React.useEffect(() => {
        const updatedValues = getSelectedDateTimeZone({
            timestamp,
            timeZone,
            defaultHourOffset,
            shouldFallbackToCurrentDateTime,
        });

        if (updatedValues.date) {
            setDate(updatedValues.date);
        }
        if (updatedValues.time) {
            setTime(updatedValues.time);
        }
        setSelectedTimeZone(updatedValues.timeZone);
    }, [timestamp, timeZone, language, defaultHourOffset, shouldFallbackToCurrentDateTime]);

    /**
     * Callback to be executed if one of the pickers change.
     */
    const onDateTimeZoneChange = React.useCallback(
        (dateTimeZone: GetSelectedDateTimeZone) => {
            const ts = getTimestampFromDateTimeZone(dateTimeZone);

            /** we return the date in a ISO timestamp format in UTC with the raw data besides it just in case  */
            onChange({
                timestamp: ts,
                data: dateTimeZone,
            });
        },
        [onChange],
    );

    /**
     * Callback to be executed once the date picker changes
     * @param changedDate - Date
     */
    const onDateChange = React.useCallback(
        (changedDate: Date | undefined) => {
            const newDate = shouldFallbackToCurrentDateTime ? changedDate || values.date : changedDate;
            setDate(newDate);
            onDateTimeZoneChange({
                date: newDate,
                timeZone: selectedTimeZone,
                time,
            });
        },
        [onDateTimeZoneChange, selectedTimeZone, shouldFallbackToCurrentDateTime, time, values.date],
    );

    /**
     * Callback to be executed once the time picker changes
     * @param changedTime - Time
     */
    const onTimeChange = React.useCallback(
        (changedTime: Time | undefined) => {
            const newTime = shouldFallbackToCurrentDateTime ? changedTime || values.time : changedTime;
            setTime(newTime);

            onDateTimeZoneChange({
                date,
                timeZone: selectedTimeZone,
                time: newTime,
            });
        },
        [date, onDateTimeZoneChange, selectedTimeZone, shouldFallbackToCurrentDateTime, values.time],
    );

    /**
     * Callback to be executed once the time zone picker changes
     * @param tz - TimeZone
     */
    const onTimeZoneChanged = React.useCallback(
        (tz: TimeZone) => {
            setSelectedTimeZone(tz);
            onDateTimeZoneChange({
                date,
                // getTimeZoneOffset is needed to return the offset on a formatted way when the user changes it
                timeZone: { ...tz, offset: getTimeZoneOffset({ timeZone: tz.id }) },
                time,
            });
        },
        [date, onDateTimeZoneChange, time],
    );

    return (
        <>
            <FlexBox
                orientation={orientation}
                {...wrapperProps}
                className={block([wrapperProps?.className])}
                fillSpace
                gap="regular"
            >
                {withDate && (
                    <DatePickerField
                        className={element('date-picker')}
                        locale={language}
                        label={translateKey(GLOBAL.DATE)}
                        nextButtonProps={{ label: translateKey(GLOBAL.NEXT_MONTH) }}
                        previousButtonProps={{ label: translateKey(GLOBAL.PREVIOUS_MONTH) }}
                        value={date}
                        onChange={onDateChange}
                        minDate={disableOptionsInThePast ? new Date() : undefined}
                        hasError={!!error}
                        {...datePickerProps}
                    />
                )}
                <TimePickerField
                    className={element('time-picker')}
                    value={time}
                    label={translateKey(GLOBAL.TIME_APPROXIMATELY)}
                    onChange={onTimeChange}
                    locale={language}
                    disableOptionsBefore={
                        disableOptionsInThePast && date && isToday(date)
                            ? getDefaultDateTimeZone({ offset: defaultHourOffset }).time
                            : undefined
                    }
                    shouldChangeWhenDisabled={disableOptionsInThePast}
                    hasError={!!error}
                    {...timePickerProps}
                />
                <TimeZonePickerField
                    className={element('time-zone-picker')}
                    onTimeZoneChanged={onTimeZoneChanged}
                    language={language}
                    label={translateKey(GLOBAL.TIMEZONE)}
                    selectedTimeZone={selectedTimeZone?.id}
                    date={date}
                    {...timeZonePickerProps}
                />
            </FlexBox>
            {!!error && (
                <InputHelper className={margin('top', 'regular')} kind={Kind.error}>
                    {error}
                </InputHelper>
            )}
        </>
    );
};
